import {
  BulkDeleteButton,
  Create,
  Datagrid,
  Edit,
  List,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
} from "react-admin";
import { CrudPermissions } from "../../auth/crud-permissions";
import PaymentIcon from "@mui/icons-material/Payment";
import { AdSources, validateAdAccountId } from "@arrowsup/platform-dtos";
import { TopHelpInfoText } from "../../help/top-help-info-text";

export const PaidSourceResource = (perms: CrudPermissions) => {
  return (
    <Resource
      name="paid-source"
      key={"paid-source"}
      options={{
        label: "Breakdowns",
      }}
      list={PaidSourceList(perms)}
      edit={perms.update ? PaidSourceEdit : undefined}
      create={perms.create ? PaidSourceCreate : undefined}
      icon={PaymentIcon}
    />
  );
};

export const PaidSourceList = (perms: CrudPermissions) => {
  return (
    <>
      <TopHelpInfoText text="Configure paid advertising sources to track and attribute conversions by account, campaign, ad group, or ad." />
      <List>
        <Datagrid
          rowClick={perms.update ? "show" : false}
          bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
        >
          <TextField source="id" label="Source ID" />
          <TextField source="name" label="Name" />
          <TextField source="adSource" label="Channel" />
          <TextField source="accountId" label="Account ID" />
          <TextField source="campaignId" label="Campaign ID" />
          <TextField source="adGroupId" label="Ad Group ID" />
          <TextField source="adId" label="Ad ID" />
        </Datagrid>
      </List>
    </>
  );
};

const AdSourceInput: React.FC = () => {
  return (
    <SelectInput
      source="adSource"
      label="Channel"
      helperText="Which channel?"
      choices={AdSources.map((v) => ({
        id: v,
        name: v,
      }))}
      fullWidth
      required
      sx={{ pb: 2 }}
    />
  );
};

const validateAccountId = (
  value: string | null | undefined,
  values: { adSource?: AdSources }
) => {
  const { adSource } = values;

  return adSource ? validateAdAccountId(adSource, value) : undefined;
};

export const AccountIdInput: React.FC = () => {
  return (
    <TextInput
      source="accountId"
      label="Account ID"
      helperText="Optional account ID of the channel, in case there are multiple. Dashes (Google) and an act_ prefix (Meta) are fine."
      validate={validateAccountId}
      fullWidth
      sx={{ pb: 2 }}
    />
  );
};

export const CampaignIdInput: React.FC = () => {
  return (
    <TextInput
      source="campaignId"
      label="Campaign ID"
      helperText="Optional campaign ID."
      fullWidth
      sx={{ pb: 2 }}
    />
  );
};

export const AdGroupId: React.FC = () => (
  <TextInput
    source="adGroupId"
    label="Ad Group ID"
    helperText="Optional Ad Group ID."
    fullWidth
    sx={{ pb: 2 }}
  />
);

export const AdId: React.FC = () => (
  <TextInput
    source="adId"
    label="Ad ID"
    helperText="Optional Ad ID."
    fullWidth
    sx={{ pb: 2 }}
  />
);

export const PaidSourceEdit: React.FC = () => {
  return (
    <div>
      <Edit redirect="list">
        <SimpleForm>
          <TextInput source="name" fullWidth />
          <AdSourceInput />
          <AccountIdInput />
          <CampaignIdInput />
          <AdGroupId />
          <AdId />
        </SimpleForm>
      </Edit>
    </div>
  );
};

export const PaidSourceCreate: React.FC = () => {
  return (
    <Create redirect="list">
      <SimpleForm>
        <TextInput source="name" fullWidth />
        <AdSourceInput />
        <AccountIdInput />
        <CampaignIdInput />
        <AdGroupId />
        <AdId />
      </SimpleForm>
    </Create>
  );
};
